import { useState } from "react";
import { sendContactMessage } from "../lib/api";
import { useSiteClub } from "../lib/SiteClubContext";
import { formatPhone } from "../components/ClubContactFields";

const PLATFORM_EMAIL = "notifications@rcracehq.com";

/** /about -- the club's details on a club's site (Manage -> Advanced
 *  settings), RC Race HQ's own on the service site. Club text is plain text
 *  and is only ever rendered as text. */
export default function AboutPage() {
  const { club, loading } = useSiteClub();
  if (loading) return <p>Loading…</p>;

  if (!club) {
    return (
      <div>
        <h1>About RC Race HQ</h1>
        <div className="card">
          <p>
            RC Race HQ runs race day for RC clubs: event announcements, race registration, and
            photo/video sharing from club race days. One account works across every club and event
            on the platform.
          </p>
          <p>
            Our first home club is the RC Car Club of Spokane (RCCCS), a community of local RC
            racers.
          </p>
        </div>
        <h2>Contact</h2>
        <div className="card">
          <p>
            Questions about an event, your registration, or the site itself? Email{" "}
            <a href={`mailto:${PLATFORM_EMAIL}`}>{PLATFORM_EMAIL}</a>, or send us a message below.
          </p>
          <ContactForm />
        </div>
      </div>
    );
  }

  const contact = club.contact ?? {};
  return (
    <div>
      <h1>About {club.name}</h1>
      {(contact.about || contact.location || contact.phone || contact.email) && (
        <div className="card">
          {contact.about && <p style={{ whiteSpace: "pre-wrap" }}>{contact.about}</p>}
          {contact.location && (
            <p>
              <strong>Location:</strong> {contact.location}
            </p>
          )}
          {contact.phone && (
            <p>
              <strong>Phone:</strong> <a href={`tel:${contact.phone}`}>{formatPhone(contact.phone)}</a>
            </p>
          )}
          {contact.email && (
            <p>
              <strong>Email:</strong> <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </p>
          )}
        </div>
      )}
      <h2>Contact</h2>
      <div className="card">
        {contact.email ? (
          <>
            <p>Questions about an event, your registration, or the club? Send {club.name} a message below.</p>
            <ContactForm clubId={club.clubId} />
          </>
        ) : (
          <p className="muted">
            {club.name} hasn't set up a contact email yet, so there's no contact form here for now. Please check
            back soon, or catch the club at the track.
          </p>
        )}
      </div>
    </div>
  );
}

/** Posts to POST /contact, which mails the club's stored contact email (or
 *  ours on the service site) -- never an address from this form. */
function ContactForm({ clubId }: { clubId?: string }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // Hidden from people; bots that fill every field get a quiet "sent".
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSending(true);
    try {
      await sendContactMessage({ clubId, name, email, subject, message: body, website });
      setSent(true);
      setSubject("");
      setBody("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Your message couldn't be sent. Please try again.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div style={{ marginTop: "1rem" }}>
        <p>Thanks, your message is on its way. Replies will come to {email}.</p>
        <button type="button" className="secondary" onClick={() => setSent(false)}>
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: "1rem" }}>
      <div className="row">
        <div>
          <label htmlFor="contact-name">Your name</label>
          <input id="contact-name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label htmlFor="contact-email">Your email</label>
          <input
            id="contact-email"
            type="email"
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
      </div>
      <div style={{ position: "absolute", left: "-10000px" }} aria-hidden="true">
        <label htmlFor="contact-website">Website</label>
        <input
          id="contact-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="contact-subject">Subject</label>
        <input id="contact-subject" maxLength={150} value={subject} onChange={(e) => setSubject(e.target.value)} required />
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="contact-body">Message</label>
        <textarea id="contact-body" rows={5} maxLength={5000} value={body} onChange={(e) => setBody(e.target.value)} required />
      </div>
      {error && <p className="err">{error}</p>}
      <button type="submit" style={{ marginTop: "0.75rem" }} disabled={sending}>
        {sending ? "Sending…" : "Send"}
      </button>
    </form>
  );
}
